/**
 * Keeps sign-in credentials out of PostHog.
 *
 * login-web hands a session to each app as `#rt=<refresh token>&t=<tenant>`.
 * That refresh token stays valid until it is revoked, so it must never reach
 * analytics: not in `$current_url`, `$referrer`, the `$initial_*` person
 * properties, a replay URL or any URL the app sends itself.
 *
 * `scrubAnalyticsEvent` is PostHog's `before_send` hook. It removes the
 * credential parameters below from every string on the event, in the query or
 * the hash, raw or percent-encoded inside another URL, and leaves the rest of
 * the string as it was. `scrubRecordedRequest` does the same for the URLs
 * session replay records.
 *
 * Settings also runs `redactSignageTokens` (redact-signage-token.ts), which
 * covers signage, visitor-link and search parameters in `properties`; this
 * covers the sign-in credentials everywhere on the event.
 */

import { redactSignageTokens } from './redact-signage-token';

/** Parameters that carry a credential, or the tenant that goes with one. */
export const CREDENTIAL_PARAMS = ['rt', 't', 'refresh_token', 'id_token', 'access_token', 'token', 'code'] as const;

const NAMES = CREDENTIAL_PARAMS.join('|');
/**
 * `?rt=…`, `#rt=…` or `&rt=…`, up to the next parameter, hash or quote. Also at
 * the very start of a string: a fragment read as a route (`rt=…&t=…`) has no
 * delimiter in front of it.
 */
const RAW = new RegExp(`(^|[?#&])(?:${NAMES})=[^&#\\s"'<>]*`, 'gi');
/** The same parameter percent-encoded inside another URL (`%23rt%3D…`). */
const ENCODED = new RegExp(`(%3F|%23|%26)(?:${NAMES})%3D(?:(?!%26|%23)[^&#\\s"'<>])*`, 'gi');

/** How far into nested properties (`$set_once`, `$initial_person_info`, …) the scrub reaches. */
const MAX_DEPTH = 4;

/** Removes credential parameters from one URL-like string; anything else comes back unchanged. */
export function scrubUrl(value: string): string {
  if (!value.includes('=') && !/%3D/i.test(value)) return value;
  const raw = value.replace(RAW, '$1');
  const out = raw.replace(ENCODED, '$1');
  if (out === value) return value;
  return out
    .replace(/^&+|([?#])&+/g, '$1')
    .replace(/&{2,}/g, '&')
    .replace(/(%3F|%23)(?:%26)+/gi, '$1')
    .replace(/(?:%26){2,}/gi, '%26')
    .replace(/(?:%3F|%23|%26)+(?=[&#\s"'<>]|$)/gi, '')
    .replace(/[?#&]+(?=[\s"'<>]|$)/g, '')
    .replace(/\?#/g, '#');
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubUrl(value);
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const next = scrubValue(value[i], depth + 1);
      if (next !== value[i]) value[i] = next;
    }
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    // Replay data is handled by scrubSnapshots: much of it is gzip text, which a pattern could corrupt.
    if (key === '$snapshot_data') continue;
    const next = scrubValue(record[key], depth + 1);
    if (next !== record[key]) record[key] = next;
  }
  return record;
}

interface RecordedEvent {
  type?: number;
  data?: { href?: unknown; payload?: { href?: unknown } };
}

/** Replay events carry the page address in two places: the meta event and the custom `$pageview`/`$url_changed` events. */
function scrubSnapshots(snapshots: unknown): void {
  if (!Array.isArray(snapshots)) return;
  for (const item of snapshots as RecordedEvent[]) {
    const data = item?.data;
    if (!data || typeof data !== 'object') continue;
    if (item.type === 4 && typeof data.href === 'string') data.href = scrubUrl(data.href);
    if (item.type === 5 && data.payload && typeof data.payload.href === 'string') {
      data.payload.href = scrubUrl(data.payload.href);
    }
  }
}

/** The fields of a PostHog event this hook reads; structurally compatible with posthog-js's `CaptureResult`. */
export interface ScrubbableEvent {
  event: string;
  properties: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
}

/**
 * PostHog `before_send`: strips credential parameters from every string in
 * the event's properties, `$set` and `$set_once` (at any depth up to
 * {@link MAX_DEPTH}), and from replay URLs.
 */
export function scrubAnalyticsEvent<T extends ScrubbableEvent>(event: T | null): T | null {
  if (!event) return event;
  if (event.properties) {
    scrubValue(event.properties, 0);
    scrubSnapshots(event.properties['$snapshot_data']);
  }
  if (event.$set) scrubValue(event.$set, 0);
  if (event.$set_once) scrubValue(event.$set_once, 0);
  return event;
}

/** Session replay's URL mask (`session_recording.maskCapturedNetworkRequestFn`). */
export function scrubRecordedRequest<T extends { name?: string; url?: string }>(request: T): T {
  if (typeof request.name === 'string') request.name = scrubUrl(request.name);
  if (typeof request.url === 'string') request.url = scrubUrl(request.url);
  return request;
}

/**
 * Settings' `before_send` (app.ts): signage, visitor-link and search
 * parameters in `properties` (redact-signage-token.ts), then sign-in
 * credentials everywhere on the event.
 */
export function settingsBeforeSend<T extends ScrubbableEvent>(event: T | null): T | null {
  if (!event) return event;
  return scrubAnalyticsEvent({ ...event, properties: redactSignageTokens(event.properties) });
}
